import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { JournalCard } from "../../components/ui/JournalCard";
import { displayToast } from "../../components/app/AppToast";
import SaveHeader from "../../components/app/headers/SaveHeader";
import ContentHeader from "../../components/app/headers/ContentHeader";
import Loader from "../../components/Loader";
import ConfirmModal from "../../components/modals/ConfirmModal";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { apiClient } from "../../services/api";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { useApiQuery, type Api } from "../../services/api";
import { goBackInApp } from "../../domains/app/utils/backNavigation";

type UserProfile = Api["get"]["/webapp/private/users/:id"]["output"]["body"];

export default function ProfileJournalPage({
  isMe,
  id,
}: {
  isMe: boolean;
  id?: string;
}) {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser, isOnboarded } = useCurrentUser();
  const userId = id;
  const [idsToUnfollow, setIdsToUnfollow] = useState<string[]>([]);
  const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);

  const targetId = isMe ? currentUser?.id : userId;

  const userQuery = useApiQuery(
    "/webapp/private/users/:id",
    targetId !== undefined && isOnboarded ? { param: { id: targetId } } : false
  );

  const user = userQuery.state === "success" ? userQuery.body : undefined;

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  function toggleUnfollow({ id }: { id: string }) {
    setIdsToUnfollow((prev) =>
      prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id]
    );
  }

  async function handleSubmit() {
    try {
      await Promise.all(
        idsToUnfollow.map((journalId) =>
          apiClient.delete("/webapp/private/users/me/journals/:journalId", {
            param: { journalId },
          })
        )
      );
      reloadCurrentUser();
      displayToast(t("Profile updated"), "success");
      goBackInApp(history, "/profile");
    } catch (error) {
      console.error("Couldn't edit journals.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  // Resolve journal list from the appropriate source
  const journals: UserProfile["journalModels"] =
    userQuery.state === "success" ? userQuery.body.journalModels : [];

  const isLoading =
    (isMe && currentUser === null) || userQuery.state === "pending";

  if (isLoading) return <Loader expanded />;

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        isMe ? (
          <SaveHeader
            title={t("My favorite journals")}
            saveReady
            onSaveClick={handleSubmit}
            onGoBack={() => {
              if (idsToUnfollow.length > 0) {
                setConfirmModalVisible(true);
              } else {
                goBackInApp(history, "/profile");
              }
            }}
          />
        ) : (
          <ContentHeader
            transparent
            title={t("{{name}}'s favorite journals", {
              name: user ? user.firstname + " " + user.lastname : "User",
            })}
          />
        )
      }
    >
      <div className="pt-[40px] px-[20px] pb-[50px]">
        {journals.map((journal, index) => {
          const active = !idsToUnfollow.includes(journal.id);
          return (
            <JournalCard
              key={journal.id}
              title={journal.name}
              onClick={() => {
                history.push("/journal/" + journal.uid);
              }}
              isLast={index === journals.length - 1}
              action={
                isMe ? (
                  <Button
                    variant={active ? "outline" : "primary"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleUnfollow(journal);
                    }}
                  >
                    {active ? t("Unfollow") : t("Follow")}
                  </Button>
                ) : undefined
              }
            />
          );
        })}
      </div>

      {isMe && (
        <ConfirmModal
          isVisible={isConfirmModalVisible}
          promptText={t(
            "Are you sure you want to exit without saving your changes?"
          )}
          onConfirm={() => {
            goBackInApp(history, "/profile");
          }}
          onCancel={() => {
            setConfirmModalVisible(false);
          }}
          confirmDanger
        />
      )}
    </ScrollableLayout>
  );
}
